import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API, useRx } from "@/context/AuthContext";

const Ctx = createContext({ quotes: {}, global: null, connected: false, feed: null, flash: {}, soundOn: false });
export const ALERT_SYMS = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX"];
const SOUND_KEY = "robox_sound_alerts";
const CFG_KEY = "robox_alert_cfg";
const loadCfg = () => { try { const c = JSON.parse(localStorage.getItem(CFG_KEY)); if (c && typeof c.level === "number" && Array.isArray(c.symbols)) return c; } catch {} return { level: 75, symbols: [...ALERT_SYMS] }; };

let audioCtx;
function chime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const t0 = audioCtx.currentTime;
    [[880, 0], [1318.5, 0.12], [1760, 0.24]].forEach(([f, d]) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0, t0 + d);
      g.gain.linearRampToValueAtTime(0.12, t0 + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 0.9);
      o.connect(g).connect(audioCtx.destination);
      o.start(t0 + d);
      o.stop(t0 + d + 1);
    });
  } catch {}
}

export function MarketProvider({ children }) {
  const { authed, token } = useRx();
  const [state, setState] = useState({ quotes: {}, global: null, connected: false, feed: null });
  const [flash, setFlash] = useState({});
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) === "1");
  const prev = useRef({});
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;
  const [alertCfg, setAlertCfgState] = useState(loadCfg);
  const cfgRef = useRef(alertCfg);
  cfgRef.current = alertCfg;
  const setAlertCfg = useCallback((c) => { localStorage.setItem(CFG_KEY, JSON.stringify(c)); setAlertCfgState(c); }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((v) => {
      const n = !v;
      localStorage.setItem(SOUND_KEY, n ? "1" : "0");
      if (n) chime();
      return n;
    });
  }, []);

  const testAlert = useCallback((sym = "NIFTY") => {
    setFlash((f) => ({ ...f, [sym]: Date.now() }));
    if (soundRef.current) chime();
  }, []);

  useEffect(() => {
    if (!authed) return;
    let ws, stopped = false, retry;
    const connect = async () => {
      const t = await token();
      if (stopped || !t) return;
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(t)}`);
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.type !== "tick") return;
        const { level, symbols } = cfgRef.current;
        const crossed = symbols.filter((s) => {
          const p = prev.current[s], n = m.quotes[s]?.score;
          return p != null && n != null && p < level && n >= level;
        });
        ALERT_SYMS.forEach((s) => { prev.current[s] = m.quotes[s]?.score; });
        if (crossed.length) {
          const now = Date.now();
          setFlash((f) => ({ ...f, ...Object.fromEntries(crossed.map((s) => [s, now])) }));
          if (soundRef.current) chime();
          crossed.forEach((s) => toast.success(`${s} Strength crossed ${level}`, { description: `Score ${m.quotes[s].score.toFixed(0)} · strong bullish signal` }));
        }
        setState({ quotes: m.quotes, global: m.global, connected: true, feed: m.feed, ts: m.ts });
      };
      ws.onclose = () => {
        setState((s) => ({ ...s, connected: false }));
        if (!stopped) retry = setTimeout(connect, 2500);
      };
    };
    connect();
    return () => { stopped = true; clearTimeout(retry); ws && ws.close(); };
  }, [authed, token]);

  return <Ctx.Provider value={{ ...state, flash, soundOn, toggleSound, testAlert, alertCfg, setAlertCfg }}>{children}</Ctx.Provider>;
}

export const useMarket = () => useContext(Ctx);

export const isFlashing = (ts, ms = 2200) => !!ts && Date.now() - ts < ms;
